<template>
  <div class="thumbnails-main">
    <div class="thumbnails-header">
      <img class="thumbnails-buttons" src="../assets/line.png" alt="solo" @click="toggleSoloLines"
           :class="{'thumbnails-solo-lines-on':soloLines}" title="Show images with lines"/>
      <img class="thumbnails-buttons" src="../assets/nolines.png" alt="no-lines" @click="toggleNoLines"
           :class="{'thumbnails-solo-nolines-on': soloNoLines}" title="Show images without lines"/>
      <img class="thumbnails-buttons" src="../assets/validate.png" alt="validated" @click="toggleValidated"
           :class="{'thumbnails-solo-validated-on': soloValidated}" title="Show validated images"/>
      <img class="thumbnails-buttons" src="../assets/invalidate.png" alt="invalidated" @click="toggleInvalidated"
           :class="{'thumbnails-solo-invalidated-on': soloInvalidated}" title="Show invalidated images"/>
    </div>

    <div class="thumbnails-content" ref="thumbsScroll" @scroll="onScroll">
      <div class="thumbnails" :style="{ '--cell': CELL_W + 'px' }">
        <div class="grid-pad" :style="{ height: padTop + 'px' }"></div>
        <div v-for="(image) in windowedImages" :key="image.id" :class="['thumbnail', {
                                                                                    'thumbnail-validated': image.is_validated && image.id !== (selectedId ?? -1),
                                                                                    'thumbnail-invalidated': image.is_invalidated,
                                                                                    'thumbnail-selected': image.id === (selectedId ?? -1) && !image.has_lines,
                                                                                    'thumbnail-has-lines': image.has_lines && image.id !== (selectedId ?? -1),
                                                                                    'thumbnail-selected-has-lines': image.has_lines && image.id === (selectedId ?? -1),
                                                                                    'thumbnail-selected-is-validated': image.is_validated && image.id === (selectedId ?? -1),
                                                                                    'thumbnail-selected-is-invalidated': image.is_invalidated && image.id === (selectedId ?? -1)}]"
            :data-image-id="image.id" @click="selectThumbnail(image)" @contextmenu.prevent="openContextMenu($event, image)">

            <img :src="thumbSrc[image.id]" :alt="image.file_name" :title="image.file_name" :width="THUMB_W" :height="THUMB_H" loading="lazy" decoding="async" fetchpriority="low" @error="onThumbError(image)"/>
            <div v-if="image.is_non_trackable" class="thumb-nontrack-x" aria-hidden="true">✕</div>
            <div class="presence-badges" v-if="presenceByImage[image.id]?.length">
              <div v-for="u in presenceByImage[image.id]" :key="u.key" class="presence-dot" :title="u.email" :style="dotStyle(u)">
                {{ (u.email || '?').charAt(0).toUpperCase() }}
              </div>
          </div>
        </div>
        <div class="grid-pad" :style="{ height: padBottom + 'px' }"></div>
      </div>
    </div>

    <div class="thumbnails-footer">
      <span class="thumbnails-footer-label">
        Images: {{ numImgs }}
      </span>
    </div>

    <div
      v-if="contextMenuVisible"
      class="context-menu"
      :style="{ top: contextMenuY + 'px', left: contextMenuX + 'px' }"
    >
      <ul>
        <li @click="toggleNonTrackable">
          {{ (selectedThumbnail?.is_non_trackable || false) ? 'Valid' : 'Invalid' }}
        </li>
        <hr>
        <li @click="deleteSelectedImages">Delete</li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts">
/**
 * Thumbnails.vue (self-contained)
 * - Maintains a base list (masterImages) and a filtered view (localImages)
 * - Subscribes to realtime via SubscriptionManager
 * - Subscribes to presence via ProjectService
 */

import { defineComponent, ref, watch, computed, onMounted, onBeforeUnmount, PropType, nextTick } from "vue";
import { ImageInfo } from "../helpers/Interfaces";
import ProjectService from "../services/Project";
import { subscriptionManager } from "@/realtime/SubscriptionManager";

export default defineComponent({
  name: "Thumbnails",
  props: {
    projectId: { type: Number as PropType<number | null>, required: false, default: null },
    selectedId: { type: Number as PropType<number | null>, default: null },
  },
  emits: ["onThumbnailSelected", "requestDelete", "requestUpload"],
  setup(props, { emit, expose }) {
    const fileInput = ref<HTMLInputElement | null>(null);

    // Base state (unfiltered) and derived view (filtered)
    const masterImages = ref<ImageInfo[]>([]); // <-- base list (added)
    const localImages  = ref<ImageInfo[]>([]); // <-- filtered view

    const selectedThumbnail = ref<ImageInfo | null>(null);

    // Filters (mutually exclusive)
    const soloLines       = ref<boolean>(false);
    const soloNoLines     = ref<boolean>(false);
    const soloValidated   = ref<boolean>(false);
    const soloInvalidated = ref<boolean>(false);

    // Presence map { image_id: [{key,email}] }
    const presenceByImage = ref<Record<number, Array<{ key: string; email: string }>>>({});

    const numImgs = computed(() => localImages.value.length);

    // Context menu
    const contextMenuVisible = ref(false);
    const contextMenuX = ref(0);
    const contextMenuY = ref(0);

    // Thumbs cache and retries
    const thumbSrc = ref<Record<number, string>>({});
    const thumbTries = ref<Record<number, number>>({});
    const thumbsScroll = ref<HTMLDivElement | null>(null);

    const COLS = 2;
    const THUMB_W = 100;
    const THUMB_H = 55;
    const PAD = 20;
    const BORDER = 2;
    const IMG_BORDER = 2;
    const CELL_W = THUMB_W + PAD*2 + BORDER*2;

    const OVERSCAN_ROWS = 8;

    const rowHeight = ref(THUMB_H + PAD*2 + BORDER*2 + IMG_BORDER*2);
    const padTop    = ref(0);
    const padBottom = ref(0);

    // janela virtual (em filas)
    const startRow = ref(0);
    const endRow   = ref(0);

    // Keep “off” functions for image realtime subscriptions
    let imgUnsubs: Array<() => void> = [];

    // Keep last projectId presence channel to clean up
    let currentPresenceProject: number | null = null;

    const startIndex = computed(() => startRow.value * COLS);
    const endIndex   = computed(() => Math.min(localImages.value.length, endRow.value * COLS));

    const windowedImages = computed(() =>
      localImages.value.slice(startIndex.value, endIndex.value)
    );

    // --- Load images (initial & refresh) ------------------------------------
    async function loadImages(projectId: number) {
        const imgs = await ProjectService.getImagesFromProject(projectId);
        // Store base list then derive the filtered view
        masterImages.value = imgs;
        localImages.value  = applyFilterToList(imgs);

        await nextTick();
        measureRowHeight();
        recalcWindow();
    }

    // --- Apply current filter to a base list --------------------------------
    function applyFilterToList(base: ImageInfo[]): ImageInfo[] {
      if (soloLines.value)       return base.filter(img => img.has_lines && !img.is_validated && !img.is_invalidated);
      if (soloNoLines.value)     return base.filter(img => !img.has_lines);
      if (soloValidated.value)   return base.filter(img => img.is_validated);
      if (soloInvalidated.value) return base.filter(img => img.is_invalidated);
      return [...base];
    }

    // Re-filter local view from the **base list**
    function applyFilter() {
      // Always derive local list from the base 'masterImages'
      localImages.value = applyFilterToList(masterImages.value);
      nextTick().then(() => {
        recalcWindow();
      });
    }

    // --- Filter toggles (mutually exclusive) --------------------------------
    function toggleSoloLines() {
      soloLines.value = !soloLines.value;
      if (soloLines.value) {
        soloNoLines.value = false; soloValidated.value = false; soloInvalidated.value = false;
      }
      applyFilter();
    }
    function toggleNoLines() {
      soloNoLines.value = !soloNoLines.value;
      if (soloNoLines.value) {
        soloLines.value = false; soloValidated.value = false; soloInvalidated.value = false;
      }
      applyFilter();
    }
    function toggleValidated() {
      soloValidated.value = !soloValidated.value;
      if (soloValidated.value) {
        soloLines.value = false; soloNoLines.value = false; soloInvalidated.value = false;
      }
      applyFilter();
    }
    function toggleInvalidated() {
      soloInvalidated.value = !soloInvalidated.value;
      if (soloInvalidated.value) {
        soloLines.value = false; soloNoLines.value = false; soloValidated.value = false;
      }
      applyFilter();
    }

    // --- Selection -----------------------------------------------------------
    async function selectThumbnail(image: ImageInfo) {
      contextMenuVisible.value = false;
      selectedThumbnail.value = image;
      
      emit("onThumbnailSelected", image);
    }

    // Keep selected from prop in sync (if parent drives it)
    watch(() => props.selectedId, (id) => {
        if (id == null) {
          ensureSelection("prop cleared");
          return;
        }
        const found = localImages.value.find(i => i.id === id) || null;
        selectedThumbnail.value = found;
      }, { immediate: true });

    // --- Context menu --------------------------------------------------------
    async function deleteSelectedImages() {
      const id = props.selectedId ?? selectedThumbnail.value?.id ?? null;
      if (!id) return;
      const confirmed = confirm("Are you sure you want to delete the selected image?");
      if (!confirmed) return;
      emit("requestDelete", id);
      contextMenuVisible.value = false;
    }
    function openContextMenu(event: MouseEvent, image: ImageInfo) {
      event.preventDefault();
      selectedThumbnail.value = image;
      contextMenuX.value = event.clientX;
      contextMenuY.value = event.clientY;
      contextMenuVisible.value = true;
    }

    // --- Upload (optional, disabled in UI by default) -----------------------
    const handleFileSelect = (event: Event) => {
      const files = (event.target as HTMLInputElement).files;
      if (!files || files.length === 0) return;
      emit("requestUpload", Array.from(files));
      (event.target as HTMLInputElement).value = "";
    };
    function uploadImages() {
      fileInput.value?.click();
    }

    // --- Thumbs helpers ------------------------------------------------------
    function primeThumb(image: ImageInfo) {
      thumbTries.value[image.id] = 0;
      thumbSrc.value[image.id] = ProjectService.getThumbUrl(image.project_id, image.file_name, 140, 78, 75);
    }

    async function onThumbError(image: ImageInfo) {
      const n = (thumbTries.value[image.id] ?? 0) + 1;
      thumbTries.value[image.id] = n;

      if (n === 1) {
        // 1st try: cache-buster
        const base = ProjectService.getThumbUrl(image.project_id, image.file_name, 140, 140, 75);
        thumbSrc.value[image.id] = `${base}${base.includes("?") ? "&" : "?"}v=${Date.now()}`;
      } else if (n === 2) {
        // 2nd try: signed URL fallback
        thumbSrc.value[image.id] = await ProjectService.getSignedThumbUrl(image.project_id, image.file_name, 60, 140, 140, 75);
      } else {
        // Last try: local placeholder to avoid infinite error loops
        thumbSrc.value[image.id] = "/broken-thumb.svg";
      }
    }

    // --- Presence badges coloring -------------------------------------------
    function hashStr(str: string): number {
      let h = 2166136261; // FNV-ish seed
      for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = (h * 16777619) >>> 0;
      }
      return h >>> 0;
    }
    function colorForUser(keyOrEmail: string): { h: number; s: number; l: number } {
      const seed = hashStr(keyOrEmail || "anon");
      const hue = Math.floor((seed * 137.508) % 360); // golden angle
      return { h: hue, s: 70, l: 50 };
    }
    function textColorForL(l: number): string {
      return l < 60 ? "#ffffff" : "#111111";
    }
    function dotStyle(u: { key: string; email: string | null }) {
      const basis = u.key || u.email || "anon";
      const { h, s, l } = colorForUser(basis);
      return {
        backgroundColor: `hsl(${h} ${s}% ${l}%)`,
        color: textColorForL(l),
        borderColor: "rgba(0,0,0,0.35)",
      };
    }

    // --- Scroll to image programmatically -----------------------------------
    type RectLike = {
      top: number; left: number; right: number; bottom: number;
      width: number; height: number;
    };

    function clamp01(v: any, def = 0.6) {
      const n = Number(v);
      if (!Number.isFinite(n)) return def;
      return Math.min(0.999, Math.max(0, n));
    }

    function viewportRect(): RectLike {
      const w = window.innerWidth || document.documentElement.clientWidth || 0;
      const h = window.innerHeight || document.documentElement.clientHeight || 0;
      return { top: 0, left: 0, right: w, bottom: h, width: w, height: h };
    }

    function waitUntilVisible(el: HTMLElement, root: HTMLElement | null, opts: { threshold?: number; timeout?: number } = {}): Promise<boolean> {
      const threshold = clamp01(opts.threshold, 0.6);
      const timeout = Number.isFinite(opts.timeout) ? (opts.timeout as number) : 4000;

      return new Promise((resolve) => {
        let done = false;
        let obs: IntersectionObserver | null = null;

        const finish = (ok: boolean) => {
          if (done) return;
          done = true;
          try { obs?.disconnect(); } catch {}
          clearTimeout(tid);
          resolve(ok);
        };

        const tid = setTimeout(() => finish(false), timeout);

        const rootBox: RectLike = root ? root.getBoundingClientRect() : viewportRect();
        const elBox = el.getBoundingClientRect();
        const w = Math.min(elBox.right, rootBox.right) - Math.max(elBox.left, rootBox.left);
        const h = Math.min(elBox.bottom, rootBox.bottom) - Math.max(elBox.top, rootBox.top);
        const interArea = Math.max(0, w) * Math.max(0, h);
        const elArea = Math.max(1, elBox.width * elBox.height);
        if (interArea / elArea >= threshold) return finish(true);

        obs = new IntersectionObserver((entries) => {
            for (const e of entries) {
              if (e.target === el && e.isIntersecting && e.intersectionRatio >= threshold) {
                finish(true);
                break;
              }
            }
          }, { root: root ?? null, threshold: [threshold] });
        obs.observe(el);
      });
    }

    // --- seleção garantida ------------------------------------------------------
    function ensureSelection(context = "ensure") {
        // se já temos selecionada e ela existe na vista filtrada, mantém
        if (selectedThumbnail.value && localImages.value.some(i => i.id === selectedThumbnail.value!.id)) {
            return;
        }
        // caso contrário, escolhe a 1ª da vista atual
        const pick = localImages.value[0] ?? null;
        if (pick) {
            selectedThumbnail.value = pick;
            emit("onThumbnailSelected", pick);
        } else {
            // lista vazia: limpa seleção local (o pai não deve depender disto)
            selectedThumbnail.value = null;
        }
    }

    function ensureSelectionAfterDelete(removedId: number, beforeList: ImageInfo[]) {
        // tenta manter posição relativa após remover
        const idx = beforeList.findIndex(i => i.id === removedId);
        const fallback =
            localImages.value[idx] || localImages.value[idx - 1] || localImages.value[0] || null;

        if (fallback) {
            selectedThumbnail.value = fallback;
            emit("onThumbnailSelected", fallback);
        } else {
            selectedThumbnail.value = null;
        }
    }

    async function scrollToImage(imageId: number) {
      const idx = localImages.value.findIndex(i => i.id === imageId);
      if (idx < 0) return;

      const row = Math.floor(idx / COLS);
      const targetTop = row * rowHeight.value;

      const root = thumbsScroll.value;
      if (!root) return;

      root.scrollTo({ top: targetTop, behavior: "smooth" });

      await nextTick();
      recalcWindow();

      setTimeout(async () => {
        const el = root.querySelector(`.thumbnail[data-image-id="${imageId}"]`) as HTMLElement | null;
        if (el) {
          el.classList.add("thumb-ping");
          setTimeout(() => el.classList.remove("thumb-ping"), 950);
        }
      }, 120);
    }

    expose({ scrollToImage });

    // --- Realtime: IMAGES via SubscriptionManager ---------------------------
    function offAllImageSubs() {
      try { imgUnsubs.forEach(off => off()); } catch {}
      imgUnsubs = [];
    }

    async function subscribeImages(projectId: number) {
        offAllImageSubs();
        const topic = subscriptionManager.topicImages(projectId);

        const mergeInto = async(id: number, patch: Partial<ImageInfo>) => {
            const idx = masterImages.value.findIndex(x => x.id === id);
            if (idx < 0) return;

            const before = masterImages.value[idx];
            const after  = { ...before, ...patch };

            // calcula se a imagem (antes/depois) passa no filtro atual
            const wasIncluded = applyFilterToList([before]).length === 1;
            const willBeIncluded = applyFilterToList([after]).length === 1;

            // atualiza a lista base
            masterImages.value.splice(idx, 1, after);

            const isSelected = selectedThumbnail.value?.id === id || props.selectedId === id;

            // caso especial: a imagem selecionada sairia do filtro -> relaxa filtros e mantém seleção
            if (isSelected && wasIncluded && !willBeIncluded) {
                // desliga TODOS os toggles de solo
                soloLines.value = false;
                soloNoLines.value = false;
                soloValidated.value = false;
                soloInvalidated.value = false;

                // re-deriva a vista sem filtros e mantém seleção
                localImages.value = applyFilterToList(masterImages.value);
                selectedThumbnail.value = after as ImageInfo;
                // opcional: garantir scroll/realce
                await nextTick();
                recalcWindow();
                scrollToImage(id);
            } else {
                // fluxo normal: só re-aplica o filtro atual
                localImages.value = applyFilterToList(masterImages.value);
                await nextTick();
                recalcWindow();                
            }
        };

        // INSERT (já estava OK)
        imgUnsubs.push(await subscriptionManager.subscribe(topic, "images:insert", async({ image }) => {
            if (!masterImages.value.some(i => i.id === image.id)) {
              masterImages.value.push(image);
              localImages.value = applyFilterToList(masterImages.value);
              await nextTick();
              recalcWindow();
              if (!selectedThumbnail.value) ensureSelection("insert");
            }
        }));

        // DELETE  ⟶ usa payload.image.id
        imgUnsubs.push(await subscriptionManager.subscribe(topic, "images:delete", async ({ image }) => {
            const id = image.id;
            const before = localImages.value.slice();
            const wasSelected = selectedThumbnail.value?.id === id || props.selectedId === id;

            masterImages.value = masterImages.value.filter(img => img.id !== id);
            localImages.value  = applyFilterToList(masterImages.value);

            delete thumbSrc.value[id];
            delete thumbTries.value[id];

            await nextTick();
            recalcWindow();

            if (wasSelected) ensureSelectionAfterDelete(id, before);
        }));

        // UPDATE genérico  ⟶ recebe { image }
        imgUnsubs.push(await subscriptionManager.subscribe(topic, "images:update", ({ image }) => {
            // podes usar o objeto inteiro como "patch"
            mergeInto(image.id, image as Partial<ImageInfo>);
            ensureSelection("update");
        }));

        // HAS_LINES  ⟶ lê image.has_lines
        imgUnsubs.push(await subscriptionManager.subscribe(topic, "images:has_lines", ({ image }) => {
            mergeInto(image.id, { has_lines: !!image.has_lines });
            ensureSelection("has_lines");
        }));

        // VALIDATED  ⟶ lê image.is_validated
        imgUnsubs.push(await subscriptionManager.subscribe(topic, "images:validated", ({ image }) => {
            mergeInto(image.id, { is_validated: !!image.is_validated });
            ensureSelection("validated");
        }));

        // INVALIDATED  ⟶ lê image.is_invalidated e image.invalidate_notes
        imgUnsubs.push(await subscriptionManager.subscribe(topic, "images:invalidated", ({ image }) => {
            mergeInto(image.id, {
            is_invalidated: !!image.is_invalidated,
            invalidate_notes: (image as any).invalidate_notes ?? null,
            });
            ensureSelection("invalidated");
        }));

        // LOCK  ⟶ lê image.review_lock_by / review_lock_until
        imgUnsubs.push(await subscriptionManager.subscribe(topic, "images:lock", ({ image }) => {
            mergeInto(image.id, {
            review_lock_by: (image as any).review_lock_by ?? null,
            review_lock_until: (image as any).review_lock_until ?? null,
            } as any);
            ensureSelection("lock");
        }));
    }

    // --- Presence: via ProjectService presence channel ----------------------
    async function subscribePresence(projectId: number) {
        // Unsubscribe previous presence channel if switching projects
        if (currentPresenceProject != null && currentPresenceProject !== projectId) {
            try {
            if (typeof (ProjectService as any).unsubscribeFromPresence === 'function') {
                await (ProjectService as any).unsubscribeFromPresence(currentPresenceProject);
            } else if (typeof (ProjectService as any).unsubscribePresence === 'function') {
                await (ProjectService as any).unsubscribePresence(currentPresenceProject);
            }
            } catch {}
        }
        currentPresenceProject = projectId;

        const cb = (map: Record<number, Array<{ key: string; email: string }>>) => {
            // Update presence map reactively
            presenceByImage.value = map || {};
        };

        if (typeof (ProjectService as any).subscribeToPresence === 'function') {
            await (ProjectService as any).subscribeToPresence(projectId, cb);
        } else if (typeof (ProjectService as any).subscribePresence === 'function') {
            await (ProjectService as any).subscribePresence(projectId, cb);
        } else {
            console.warn('[Thumbnails] Presence subscription method not found on ProjectService.');
        }
    }

    function measureRowHeight() {
      rowHeight.value = THUMB_H + PAD*2 + BORDER*2 + IMG_BORDER*2;
    }

    function ensurePrimedForWindow() {
      for (let i = startIndex.value; i < endIndex.value; i++) {
        const img = localImages.value[i];
        if (!img) continue;
        if (!thumbSrc.value[img.id]) primeThumb(img); // só carrega quando entra na janela
      }
    }

    function recalcWindow() {
      const root = thumbsScroll.value;
      if (!root) return;

      // linhas visíveis no container + overscan
      const ch = root.clientHeight || 0;
      const st = root.scrollTop || 0;

      const rowsInView = Math.max(1, Math.ceil(ch / rowHeight.value));
      const first = Math.max(0, Math.floor(st / rowHeight.value) - OVERSCAN_ROWS);
      const totalRows = Math.ceil(localImages.value.length / COLS);
      const last = Math.min(totalRows, Math.floor(st / rowHeight.value) + rowsInView + OVERSCAN_ROWS);

      startRow.value = first;
      endRow.value   = last;

      padTop.value    = first * rowHeight.value;
      padBottom.value = Math.max(0, (totalRows - last) * rowHeight.value);

      ensurePrimedForWindow();
    }

    async function toggleNonTrackable() {
      const id = selectedThumbnail.value?.id ?? props.selectedId ?? null;
      if (!id) return;

      const current = !!selectedThumbnail.value?.is_non_trackable;
      const next = !current;

      try {
        if (selectedThumbnail.value) selectedThumbnail.value.is_non_trackable = next;

        await ProjectService.setImageNonTrackable(id, next);

      } catch (e) {
        console.error('Falha a alternar is_non_trackable:', e);
        // rollback
        if (selectedThumbnail.value) selectedThumbnail.value.is_non_trackable = current;
        alert('Falha a alterar estado (non-trackable).');
      } finally {
        contextMenuVisible.value = false;
      }
    }

    function onScroll() {
      recalcWindow();
    }

    // --- Lifecycle -----------------------------------------------------------
    async function boot(projectId: number) {
      await loadImages(projectId);
      await subscribeImages(projectId);
      await subscribePresence(projectId);

      await nextTick();
      measureRowHeight();
      recalcWindow();      
    }

    watch(
    () => props.projectId,
    async (pid) => {
        
        localImages.value = [];
        thumbSrc.value = {};
        thumbTries.value = {};
        selectedThumbnail.value = null;
        presenceByImage.value = {};
        offAllImageSubs();
        
        if (!pid || pid <= 0) {
        return;
        }
        await boot(pid);
    }, { immediate: true });

    onMounted(() => { 
      const onResize = () => {
        measureRowHeight();
        recalcWindow();
      };
      window.addEventListener('resize', onResize);
      (window as any).__thumbs_onResize = onResize;      
     });

    onBeforeUnmount(async () => {
      offAllImageSubs();
      if (currentPresenceProject != null) {
        try {
          if (typeof (ProjectService as any).unsubscribeFromPresence === 'function') {
            await (ProjectService as any).unsubscribeFromPresence(currentPresenceProject);
          } else if (typeof (ProjectService as any).unsubscribePresence === 'function') {
            await (ProjectService as any).unsubscribePresence(currentPresenceProject);
          }
        } catch {}
      }
      if ((window as any).__presenceBump) {
        document.removeEventListener('visibilitychange', (window as any).__presenceBump);
        delete (window as any).__presenceBump;
      }
      const h = (window as any).__thumbs_onResize;
      if (h) window.removeEventListener('resize', h);      
    });

    return {
      // data
      localImages,
      selectedThumbnail,
      numImgs,
      presenceByImage, // <-- keep ref, not snapshot
      // filters
      soloLines,
      soloNoLines,
      soloValidated,
      soloInvalidated,
      toggleSoloLines,
      toggleNoLines,
      toggleValidated,
      toggleInvalidated,
      // selection + menu
      selectThumbnail,
      contextMenuVisible,
      contextMenuX,
      contextMenuY,
      openContextMenu,
      deleteSelectedImages,
      // uploads
      uploadImages,
      handleFileSelect,
      fileInput,
      // thumbs
      thumbsScroll,
      thumbSrc,
      onThumbError,
      dotStyle,
      // expose
      scrollToImage,
      THUMB_W,
      THUMB_H,
      padTop,
      padBottom,
      CELL_W,
      windowedImages,
      onScroll,
      toggleNonTrackable
    };
  },
});
</script>

<style lang="scss" scoped>
    // root node
    .thumbnails-main {
        display: flex;
        flex-direction: column;
        height: 100%; // Occupies full height of the parent
        width: 100%; // Occupies full width of the parent
        overflow: hidden; // Hide overflow to contain content
        position: relative; // Position is set to relative to follow parent css
    }

    .thumbnails-header {
        flex-shrink: 0;
        height: 30px;
        background-color: rgb(56, 56, 56);
        display: flex;
        align-items: center;
        justify-content: start;
        z-index: 1; // Make sure header stays on top when on scroll
        border-bottom: 1px solid #9e9e9e;
    }
    
    .thumbnails-buttons {
        padding: 3px;
        width: 20px;
        height: auto;
        margin-left: 10px;
        border: 2px solid transparent;
    }

    .thumbnails-solo-lines-on,
    .thumbnails-solo-nolines-on,
    .thumbnails-solo-validated-on,
    .thumbnails-solo-invalidated-on,
    .thumbnails-buttons:hover {
        background: #464646;
        border: 2px solid #767676;
    }

    .thumbnails-footer {
        flex-shrink: 0;
        height: 30px;
        background-color: rgb(56, 56, 56);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1; // Make sure footer stays on top when on scroll
        border-top: 1px solid #9e9e9e;
    }

    .thumbnails-content {
        flex-grow: 1;
        flex-shrink: 1;
        min-height: 0;
        overflow-y: auto;
        position: relative;
        scrollbar-gutter: stable; /* Helps prevent content shift when scrollbar appears */
        padding-right: 10px; /* Space for scrollbar */
        scrollbar-width: thin;          /* "auto" or "thin" */
        scrollbar-color: rgb(80, 80, 80) transparent;   /* scroll thumb and track */ 
        overflow: overlay;
    }

    .thumbnails {
        display: grid;
        grid-template-columns: repeat(2, var(--cell));
        gap: 0;
        background-color: rgb(36, 36, 36);
    }

    .grid-pad
    {
      grid-column: 1 / -1; 
    }

    .thumbnail {
      width: var(--cell);
      box-sizing: border-box;
      padding: 20px;
      border: 2px solid black;
      border-top: 2px solid transparent;
      position: relative;
    }

    .thumbnail img {
      display: block;
      width: 100%;
      height: auto;
      aspect-ratio: 140 / 78;
      cursor: pointer;
      user-select: none;
      transition: transform 0.2s;
      border: 2px solid transparent;
    }

    // Highlight when a thumbnail is hovered (mouse enter component area)
    .thumbnail img:hover {
        transform: scale(1.1);
    }

    // Shows the thumbnails with lines
    .thumbnail-has-lines {
        img {
            border: 2px solid orange;
        }
    }

    // Highlight when a thumbnail is selected
    .thumbnail-selected {
        img {
            transform: scale(1.1);
            box-shadow: 3px 3px 5px rgb(168, 168, 168);
        }
    }

    .thumbnail-selected-has-lines {
        img {
            transform: scale(1.1);
            border: 2px solid orange;
            box-shadow: 3px 3px 5px rgb(168, 168, 168);
        }
    }

    .thumbnail-locked {
        pointer-events: none;
        opacity: 0.5;
        position: relative;
    }

    .thumbnail-locked::after {
        content: '🔒';
        position: absolute;
        top: 5px;
        left: 5px;
        font-size: 24px;
        color: red;
    }

    .thumbnails-footer-label {
        font-size: small;
        color: white;
    }

    .thumbnails-content::-webkit-scrollbar {
        width: 12px;
    }

    .thumbnails-content::-webkit-scrollbar-track {
        background: transparent;        /* color of the tracking area */
    }

    .thumbnails-content::-webkit-scrollbar-thumb {
        background-clip: content-box;
        background-color: rgb(80, 80, 80);    /* color of the scroll thumb */
    }

    .thumbnails-content::-webkit-scrollbar-thumb:hover {
        background-color: rgb(180, 180, 180);    /* color of the scroll thumb */
    }

    .context-menu {
        position: absolute;
        background: #333;
        color: white;
        padding: 8px;
        border-radius: 4px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
        z-index: 1000;
    }

    .context-menu ul {
        list-style: none;
        padding: 0;
        margin: 0;
    }

    .context-menu li {
        padding: 5px 10px;
        cursor: pointer;
    }

    .context-menu li:hover {
        background: #555;
    }

    .presence-badges {
        position: absolute;
        left: 6px;
        bottom: 6px;
        display: flex;
        gap: 4px;
        max-width: calc(100% - 12px);
        overflow: hidden; 
        z-index: 2;
    }

    .presence-dot {
        width: 18px;
        height: 18px;
        border-radius: 50%;
        font-size: 11px;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid rgba(0,0,0,0.35);
        user-select: none;
    }

    /* highlight forte por cima do thumbnail */
    .thumbnail.thumb-ping::after {
        content: "";
        position: absolute;
        inset: 6px;                /* margem interna para não colar à borda */
        border: 3px solid #ffb703; /* cor dos badges para consistência */
        border-radius: 6px;
        pointer-events: none;
        animation: thumbPing 900ms ease-out 1 forwards;
        //box-shadow: 0 0 12px rgba(255, 183, 3, .6);
    }

    /* pulso rápido que fadeia */
    @keyframes thumbPing {
        0%   { opacity: 1; transform: scale(1); }
        60%  { opacity: 1; transform: scale(1.02); }
        100% { opacity: 0; transform: scale(1.02); }
    }

    .sr-only {
        position: absolute !important;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0,0,0,0);
        white-space: nowrap;
        border: 0;
    }
    
    .thumbnail-validated img {
        border: 2px solid #22c55e;
        box-shadow: 0 0 0 2px rgba(34,197,94,.35); 
    }

    .thumbnail-selected-is-validated {
        img {
            transform: scale(1.1);
            border: 2px solid #22c55e;
            box-shadow: 3px 3px 5px rgb(168, 168, 168);
        }
    }

    .thumbnail-invalidated img {
        border: 2px solid #ef4444;
        box-shadow: 0 0 0 2px rgba(239,68,68,.35);
    }

    .thumbnail-selected-is-invalidated {
        img {
            transform: scale(1.1);
            border: 2px solid #ef4444;
            box-shadow: 3px 3px 5px rgb(168, 168, 168);
        }
    }

    .thumb-nontrack-x {
      position: absolute;
      inset: 0;
      z-index: 4;
      pointer-events: none;
      user-select: none;

      display: flex;
      align-items: center;
      justify-content: center;

      font-size: 46px;
      font-weight: 900;
      color: rgba(239, 68, 68, 0.85);
      text-shadow: 0 2px 14px rgba(0,0,0,0.7);
    }
</style>